import { LinearProgress } from "@material-ui/core";
import React, { useState } from "react";
import API from "../../../../../app/utils/api";
import FileUploader from "../../../../../app/components/file.uploader";
import CustomModal from "../../../../../app/components/modal.no.close";
import styled from "styled-components";
import { useSnackbar } from "../../../../../app/contexts/snackbar.context";
import { useCurrency } from "../../../../../app/contexts/currency.context";
import { useScheduleEvents } from "../../../../../app/contexts/schedule.events.context";
import * as XLSX from "xlsx";
import PrimaryButton from "../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../app/components/buttons/outlined";
import { processPerProcedureData } from "./process.per-procedure.data";
import { processSiteFeesData } from "./process.site-fees.data";

const UploadContractModal = ({ 
  onContinue, 
  studyId, 
  scheduleId, 
  siteGroupId, 
  siteId, 
  siteNumber,
  open,
  onClose,
}) => {
  const { showSnackbar } = useSnackbar();
  const { currencies } = useCurrency();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const { scheduleOfEventsVisits } = useScheduleEvents();

  const handleChange = async (files) => {
    if (files.length === 0) return;
    handleUpload(files);
  };

  const handleUpload = async (files) => {
    if (files.length === 0) return;

    setLoading(true);
    setProgress(0);
    setStatus("Processing site contract...");
    setUploadErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          cellDates: true,
          cellNF: true,
          cellStyles: true,
        });

        // Find the 'Per Procedure' sheet
        const perProcedureWorksheet = workbook.Sheets["Per Procedure"];
        if (!perProcedureWorksheet) {
          showSnackbar(
            "Could not find 'Per Procedure' sheet in the workbook",
            "error",
          );
          setLoading(false);
          setUploadErrors([
            "Could not find 'Per Procedure' sheet in the workbook",
          ]);
          return;
        }

        // Find the 'Invoiceables and Site Fees' sheet
        const siteFeesWorksheet = workbook.Sheets["Invoiceables and Site Fees"];
        if (!siteFeesWorksheet) {
          showSnackbar(
            "The 'Invoiceables and Site Fees' sheet was not found. Site costs have not been imported.",
            "warning",
          );
        }

        try {
          const perProcedureData = processPerProcedureData(perProcedureWorksheet, showSnackbar, scheduleOfEventsVisits, currencies);
          const siteAssessments = processSiteFeesData(siteFeesWorksheet, perProcedureData?.currency, showSnackbar);

          if (perProcedureData?.assessments?.length === 0) {
            showSnackbar(
              "No valid assessment codes were found in Column A of the 'Per Procedure' sheet. Assessment Costs will not be uploaded.",
              "warning",
            );
          }

          if (perProcedureData?.error) {
            setLoading(false);
            setUploadErrors([perProcedureData.error]);
            return;
          }

          const siteData = {
            scheduleOfEventsId: scheduleId,
            siteNumber: siteNumber,
            siteId: siteId,
            assessments: [...perProcedureData?.assessments, ...siteAssessments],
            visits: perProcedureData?.visits,
          };

          setProgress(50);
          const response = await API.uploadContract(studyId, siteData);
          if (perProcedureData?.siteDate) {
            const input = {
              siteId,
              siteContractEffectiveDate: perProcedureData.siteDate,
              siteGroupId,
              scheduleOfEventsId: scheduleId,
            };
            await API.setSOESiteContractDate(input, studyId);
          }

          if (response.status === 200) {
            showSnackbar("Site contract uploaded successfully", "success");
            setProgress(100);
            setStatus("Upload completed.");
            setApiStatus(200);
          } else {
            showSnackbar("Failed to upload site contract", "error");
            setProgress(100);
            setStatus("Upload failed.");
            setApiStatus(response.status);
            setUploadErrors([
              response.data?.message || "Unknown error occurred",
            ]);
          }
        } catch (error) {
          setUploadErrors([error.message]);
          setProgress(100);
          setStatus("Upload failed.");
          setApiStatus("error");
        }
      };
      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      showSnackbar("Failed to process file", "error");
      setLoading(false);
      setUploadErrors([error.message]);
    }
  };

  const handleContinue = () => {
    onClose();
    onContinue();
  };

  return (
    <CustomModal
      size={700}
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="Upload Site Contract"
      aria-describedby="upload-contract"
      title="Upload Site Contract"
    >
      <Container>
        {!loading && (
          <VContainer>
            <FileUploader
              acceptedFiles={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [],
              }}
              onFileUpload={handleChange}
            />
          </VContainer>
        )}
        {loading && (
          <VContainer>
            {status !== "Upload completed." && status !== "Upload failed." && (
              <LinearProgress
                variant="determinate"
                value={progress}
              />
            )}
            <StatusText>{status}</StatusText>
            {uploadErrors.length > 0 && (
              <ErrorContainer>
                <ErrorTitle>Errors Found:</ErrorTitle>
                {uploadErrors.map((error, index) => (
                  <ErrorText key={index}>{error}</ErrorText>
                ))}
              </ErrorContainer>
            )}
          </VContainer>
        )}
        <ButtonContainer>
          {loading && apiStatus === 200 ? (
            <PrimaryButton onClick={handleContinue}>Continue</PrimaryButton>
          ) : (
            <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          )}
        </ButtonContainer>
      </Container>
    </CustomModal>
  );
};

export default UploadContractModal;

const VContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding-top: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding: 1em;
  padding-bottom: 0em;
  padding-top: 0;
  max-height: 75vh;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
`;

const StatusText = styled.div`
  font-weight: 500;
  margin-bottom: 1em;
`;

const ErrorContainer = styled.div`
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  padding: 1em;
  margin-top: 1em;
`;

const ErrorTitle = styled.div`
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 0.5em;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.9em;
  margin-bottom: 0.25em;
`;
